import { useState } from 'react';
import {
  Card, CardContent, CardHeader, Stack, TextField, MenuItem, Button, Chip, Table, TableHead, TableRow, TableCell, TableBody, FormGroup, FormControlLabel, Checkbox, Typography,
} from '@mui/material';
import { Download } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../../components/PageHeader';
import { EmptyState } from '../../../components/EmptyState';
import { amcAnalyticsApi, type ReportResult } from '../../../api/evcpdAmc.api';

export function AmcReportsPage() {
  const { data: types } = useQuery({ queryKey: ['amc-report-types'], queryFn: () => amcAnalyticsApi.reportTypes() });
  const [type, setType] = useState('amc');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [cols, setCols] = useState<string[]>([]);
  const [result, setResult] = useState<ReportResult | null>(null);

  const meta = types?.find((t) => t.type === type);
  const params = { from: from || undefined, to: to || undefined, columns: cols.length ? cols : undefined };

  const run = async () => setResult(await amcAnalyticsApi.report(type, params));
  const toggleCol = (key: string) => setCols((c) => (c.includes(key) ? c.filter((x) => x !== key) : [...c, key]));

  return (
    <>
      <PageHeader title="AMC Reports" subtitle="AMC, PM, SLA, renewal, revenue and technician reports — exportable to Excel & PDF." />
      <Card sx={{ mb: 2 }}>
        <CardHeader title="Report builder" titleTypographyProps={{ variant: 'subtitle1' }} />
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <TextField select size="small" label="Report" value={type} onChange={(e) => { setType(e.target.value); setCols([]); setResult(null); }} sx={{ minWidth: 220 }}>
                {(types ?? []).map((t) => <MenuItem key={t.type} value={t.type}>{t.title}</MenuItem>)}
              </TextField>
              <TextField size="small" label="From" type="date" InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} />
              <TextField size="small" label="To" type="date" InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)} />
              <Button variant="contained" onClick={run}>Preview</Button>
            </Stack>
            {meta && (
              <>
                <Typography variant="caption" color="text.secondary">Columns (all if none selected)</Typography>
                <FormGroup row>
                  {meta.columns.map((c) => <FormControlLabel key={c.key} control={<Checkbox size="small" checked={cols.includes(c.key)} onChange={() => toggleCol(c.key)} />} label={c.header} />)}
                </FormGroup>
              </>
            )}
            <Stack direction="row" spacing={1}>
              <Button startIcon={<Download />} variant="outlined" onClick={() => amcAnalyticsApi.download(`/evcpd-amc/reports/${type}`, 'xlsx', params)}>Excel</Button>
              <Button startIcon={<Download />} variant="outlined" onClick={() => amcAnalyticsApi.download(`/evcpd-amc/reports/${type}`, 'pdf', params)}>PDF</Button>
              <Button startIcon={<Download />} variant="outlined" color="secondary" onClick={() => amcAnalyticsApi.download('/evcpd-amc/reports/export', 'xlsx', params)}>Export All (Excel)</Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title={result ? `${result.title} — ${result.rows.length} rows` : 'Preview'} titleTypographyProps={{ variant: 'subtitle1' }} />
        <CardContent sx={{ p: 0, overflowX: 'auto' }}>
          {!result ? <EmptyState title="Run a preview to see report data" dense /> : result.rows.length === 0 ? <EmptyState title="No rows for the selected filters" dense /> : (
            <Table size="small">
              <TableHead><TableRow>{result.columns.map((c) => <TableCell key={c.key}>{c.header}</TableCell>)}</TableRow></TableHead>
              <TableBody>
                {result.rows.slice(0, 100).map((row, i) => (
                  <TableRow key={i} hover>{result.columns.map((c) => <TableCell key={c.key}>{String(row[c.key] ?? '—')}</TableCell>)}</TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {result && result.rows.length > 100 && <Chip size="small" sx={{ m: 1 }} label={`Showing 100 of ${result.rows.length} — export for full data`} />}
        </CardContent>
      </Card>
    </>
  );
}
